// Facturación de un pedido de venta.
//
// La vista de líneas tiene una fila por FACTURA de cada línea, más una fila con
// el resto pendiente de facturar. Así que una misma línea aparece varias veces,
// y de ahí salen las dos cosas que necesita la pantalla: el resumen por factura
// y el pendiente que iba quedando factura a factura.
//
// Las cantidades de nivel línea (`qtyLinea`, `qtySinFacturar`) ya NO se derivan
// aquí: vienen calculadas en la tabla de Fabric. Antes se reconstruían sumando
// filas y agrupando por `numLinea`, y esa clave no vale (ver `clave`).
import type { PedidoVentaLinea } from '@/types'

export interface ResumenFactura {
  factura: string
  /** Clave estable de la factura, para pedir sus adjuntos. */
  recid?: string
  fecha?: string
  /** Nº de filas de la factura. */
  lineas: number
  /** Sus filas, para el desplegable. */
  filas: PedidoVentaLinea[]
  qtyFacturada: number
  importeFacturado: number
  moneda?: string
}

/** Identidad de la línea de pedido.
 *
 *  NO puede ser `numLinea`: hay pedidos importados en los que TODAS las líneas
 *  llegan con `num_linea = 1` —verificado, 9.369 líneas, el 2% del maestro, con
 *  casos de 47 artículos distintos compartiendo el 1—. Agrupar por ese número
 *  fusionaría artículos que no tienen nada que ver.
 *
 *  `inventtransid` es la clave real de la línea en D365, y la que usan también
 *  las facturas (`custinvoicetrans`) y las cargas. */
const clave = (l: PedidoVentaLinea) => `${l.inventtransid ?? l.numLinea ?? ''}`

/** Pendiente que quedaba tras cada factura: cantidad de la línea menos lo
 *  servido hasta esa factura inclusive, recorriéndolas por fecha.
 *
 *  Alimenta sólo el tooltip de PEND. FACTURAR. La columna muestra lo que falta
 *  HOY (`qtySinFacturar`), que es el mismo número en todas las facturas de la
 *  línea; este acumulado es el histórico, que ahí ya no cabe.
 *
 *  Se indexa por identidad de objeto: las filas vienen del mismo array de la
 *  caché de la query, así que la referencia es estable y no hace falta inventar
 *  una clave compuesta que podría colisionar.
 */
export function pendienteTrasFactura(
  lineas: PedidoVentaLinea[],
): Map<PedidoVentaLinea, number> {
  // Sólo las facturadas participan del acumulado, agrupadas por línea.
  const porLinea = new Map<string, PedidoVentaLinea[]>()
  for (const l of lineas) {
    if (!l.factura?.trim()) continue
    const k = clave(l)
    const arr = porLinea.get(k)
    if (arr) arr.push(l); else porLinea.set(k, [l])
  }

  const out = new Map<PedidoVentaLinea, number>()
  for (const filas of porLinea.values()) {
    const orden = [...filas].sort((a, b) =>
      (a.primeraFechaFactura ?? '').localeCompare(b.primeraFechaFactura ?? '') ||
      (a.factura ?? '').localeCompare(b.factura ?? ''),
    )
    let servido = 0
    for (const f of orden) {
      servido += f.qtyServida
      // Nunca negativo: si se sirviera de más, el pendiente es cero, no un
      // número rojo que confundiría más que la columna que vino a sustituir.
      out.set(f, Math.max(0, f.qtyLinea - servido))
    }
  }
  return out
}

/** Una entrada por factura, ordenada por fecha. Las líneas sin facturar quedan
 *  fuera: su sitio es la banda "sin facturar" de la tabla. */
export function agruparPorFactura(lineas: PedidoVentaLinea[]): ResumenFactura[] {
  const map = new Map<string, PedidoVentaLinea[]>()
  for (const l of lineas) {
    const f = l.factura?.trim()
    if (!f) continue
    const arr = map.get(f)
    if (arr) arr.push(l); else map.set(f, [l])
  }
  return [...map.entries()]
    .map(([factura, ls]) => ({
      factura,
      // El recid es por fila, no por cabecera de factura: vale el de la primera
      // que lo traiga, que es lo que el flujo de adjuntos necesita.
      recid: ls.find(l => l.recidFactura?.trim())?.recidFactura,
      fecha: ls.find(l => l.primeraFechaFactura)?.primeraFechaFactura,
      lineas: ls.length,
      // Por nº de línea y, a igualdad, por artículo: en los pedidos importados
      // todas las líneas comparten el 1 y sin el segundo criterio el orden
      // quedaría al azar entre renders.
      filas: [...ls].sort((a, b) =>
        (a.numLinea ?? 0) - (b.numLinea ?? 0) ||
        (a.articulo ?? '').localeCompare(b.articulo ?? '', 'es-ES'),
      ),
      qtyFacturada: ls.reduce((s, l) => s + (l.qtyFacturada ?? 0), 0),
      importeFacturado: ls.reduce((s, l) => s + (l.importeFacturado ?? 0), 0),
      moneda: ls.find(l => l.moneda)?.moneda,
    }))
    .sort((a, b) =>
      (a.fecha ?? '').localeCompare(b.fecha ?? '') ||
      a.factura.localeCompare(b.factura, 'es-ES'),
    )
}

/** Totales del pedido para la cabecera del bloque. */
export function totalFacturado(lineas: PedidoVentaLinea[]) {
  const facturada = lineas.reduce((s, l) => s + (l.qtyFacturada ?? 0), 0)
  const pedida = lineas.reduce((s, l) => s + l.qtyPedida, 0)
  const importe = lineas.reduce((s, l) => s + (l.importeFacturado ?? 0), 0)
  return {
    facturada,
    pedida,
    importe,
    pct: pedida > 0 ? (facturada / pedida) * 100 : 0,
  }
}
